// ============================================================================
// AJUSTES DEL BOT (C2-D7) — la pantalla del DUEÑO del negocio.
//
// ⭐⭐ T-IDE-8 (31-ago-2026) · AQUÍ VIVÍA UNA NOTA QUE DECÍA POR QUÉ ESTA
// PANTALLA TENÍA UN SOLO INTERRUPTOR: «la tabla `perfil_bot` tiene además
// nombre, cargo, tono, emojis y firma… y HOY NO LOS LEE NADIE». Eso dejó de ser
// verdad con T-MOT-2/T-IDE-3: el motor lee los seis campos de la BASE
// (`worker/src/ia/plantilla_base.ts:construirSystem`), y el 31-ago se comprobó
// en producción —la fila decía «Armando II» y el worker lo tomó solo en su
// refresco, sin desplegar—. Por eso los campos entran ahora y no antes: la regla
// que mandaba era «un botón que miente es peor que un botón que falta» (R-IDE-02),
// y hasta hoy habrían mentido.
//
// ⛔⛔ LA SECCIÓN «QUIÉN ES TU BOT» SE PINTA RECORRIENDO `CAMPOS_DE_IDENTIDAD`
// (`tipos.ts`), NO CON SEIS BLOQUES ESCRITOS A MANO. Esa lista es lo que la vara
// V-19 recorre para exigir que cada campo pintado llegue al system prompt. Si
// alguien vuelve a escribirlos a mano, V-19 sigue verde mientras se cuela un
// campo bonito que el bot ignora — que es justo el defecto que R-IDE-02 prohíbe.
//
// ⚠️ LOS `maxLength` DE AQUÍ SON COMODIDAD, NO DEFENSA. `cargo` y `firma` viajan
// al system prompt del bot real, y la sesión del dueño puede escribir en
// `perfil_bot` por la API sin pasar por esta pantalla (policy `acceso_dueno`,
// migr `0004:169`). La baranda de verdad está en el MOTOR
// (`plantilla_base.ts:TOPES_IDENTIDAD`), que es la puerta por la que pasan el
// worker y el simulador. Lo mide V-20.
//
// Lo que NUNCA va a entrar: prompt, reglas de precio, motores y canales son
// solo-Victorix por RLS (matriz de roles, 0004). Por mal que se llene esta
// pantalla, el motor no se puede romper.
// ============================================================================
import { useCallback, useEffect, useState } from "react";
import { Check, Keyboard, UserRound } from "lucide-react";
import { Aviso, Boton, Campo, Interruptor, Lista, SkeletonLista } from "./piezas.tsx";
import { explicarError, guardarPerfilBot, leerPerfilBot } from "./datos.ts";
import { CAMPOS_DE_IDENTIDAD, type CampoDeIdentidad } from "./tipos.ts";
import type { ContextoPanel, PerfilBot } from "./tipos.ts";

/**
 * Cómo se pinta CADA campo de `CAMPOS_DE_IDENTIDAD`. Es un `Record` sobre el
 * tipo y no un objeto suelto a propósito: añadir un campo a la lista sin
 * describirlo aquí **no compila**, así que no existe el estado intermedio de
 * «está en la lista pero no se pinta».
 *
 * ⚠️ Los topes son los mismos de `TOPES_IDENTIDAD` en el motor. Si algún día se
 * separan, el motor sigue recortando: el peor caso es que aquí se deje escribir
 * de más y el bot lea de menos, nunca al revés.
 */
type Descriptor =
  | { tipo: "texto"; etiqueta: string; ayuda: string; max: number; requerido?: boolean; ejemplo?: string }
  | { tipo: "lista"; etiqueta: string; ayuda: string; opciones: ReadonlyArray<{ valor: string; etiqueta: string }> }
  | { tipo: "interruptor"; etiqueta: string; ayuda: string };

const DESCRIPTORES: Record<CampoDeIdentidad, Descriptor> = {
  nombre_bot: {
    tipo: "texto", etiqueta: "Nombre", requerido: true, max: 40, ejemplo: "Armando",
    ayuda: "Con este nombre se presenta el bot cuando un cliente le escribe.",
  },
  cargo: {
    tipo: "texto", etiqueta: "Cargo", max: 60, ejemplo: "asesor de ventas",
    ayuda: "Va justo después del nombre: «Te llamas Armando, asesor de ventas».",
  },
  tono: {
    tipo: "lista", etiqueta: "Tono",
    ayuda: "Cómo le habla a tus clientes. No cambia lo que dice, cambia cómo lo dice.",
    opciones: [
      { valor: "formal", etiqueta: "Formal — trata de usted, sin confianzas" },
      { valor: "cercano", etiqueta: "Cercano — amable y natural" },
      { valor: "vendedor", etiqueta: "Vendedor — empuja hacia la compra" },
    ],
  },
  emojis: {
    tipo: "interruptor", etiqueta: "Puede usar emojis",
    ayuda: "Apagado, el bot escribe sin un solo emoji.",
  },
  firma: {
    tipo: "texto", etiqueta: "Firma", max: 120, ejemplo: "— Living House",
    ayuda: "Con qué cierra sus mensajes. Déjalo vacío si no quieres que firme.",
  },
  idioma: {
    tipo: "lista", etiqueta: "Idioma",
    ayuda: "El español que usa. «es-CO» es el de Colombia.",
    opciones: [
      { valor: "es-CO", etiqueta: "Español de Colombia (es-CO)" },
      { valor: "es-MX", etiqueta: "Español de México (es-MX)" },
      { valor: "es-ES", etiqueta: "Español de España (es-ES)" },
      { valor: "en-US", etiqueta: "Inglés de Estados Unidos (en-US)" },
    ],
  },
};

/** El perfil que se pinta cuando el negocio todavía no tiene fila (alta posterior a la 0017). */
function perfilPorDefecto(tenantId: string): PerfilBot {
  return {
    tenant_id: tenantId, nombre_bot: "Asistente", cargo: null, tono: "cercano",
    emojis: true, firma: null, idioma: "es-CO", indicador_escritura: true,
  };
}

export function Ajustes({ ctx }: { ctx: ContextoPanel }) {
  const { sb, tenantId, rol } = ctx;
  const puedeEditar = rol === "dueno" || rol === "victorix";

  const [perfil, setPerfil] = useState<PerfilBot | null>(null);
  /**
   * ⛔⛔ SOLO LO QUE CAMBIÓ, Y POR ESO ESTO ES UN ESTADO APARTE (V-17).
   * `guardarPerfilBot` acepta un `Partial` y hace `upsert`: mandar el objeto
   * entero significaría que, con dos personas del negocio editando a la vez, el
   * que guarda de segundo PISA el cambio del primero con lo que tenía cargado en
   * pantalla — sin un solo error y sin que nadie se entere.
   */
  const [cambios, setCambios] = useState<Partial<PerfilBot>>({});
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recargar = useCallback(async () => {
    setCargando(true);
    try {
      const p = await leerPerfilBot(sb, tenantId);
      // Sin fila todavía (negocio dado de alta después de 0017): se muestran los
      // mismos valores por defecto de la tabla, para que la pantalla no aparezca
      // vacía y desconcertante.
      setPerfil(p ?? perfilPorDefecto(tenantId));
      setCambios({});
      setError(null);
    } catch (e) {
      setError(explicarError(e));
    } finally {
      setCargando(false);
    }
  }, [sb, tenantId]);

  useEffect(() => { void recargar(); }, [recargar]);

  /** Un campo tocado: se pinta al instante y se APUNTA como pendiente de guardar. */
  function editar<K extends keyof PerfilBot>(campo: K, valor: PerfilBot[K]) {
    setPerfil((p) => (p ? { ...p, [campo]: valor } : p));
    setCambios((c) => ({ ...c, [campo]: valor }));
    setGuardado(false);
  }

  const nombreVacio = !(perfil?.nombre_bot ?? "").trim();

  async function guardar() {
    if (!perfil || guardando) return;
    // El nombre es obligatorio (1..40): sin él, `construirSystem` armaría un
    // «Te llamas .» y el bot se presentaría sin nombre. Se frena aquí y se dice
    // por qué, en vez de dejar que la base lo acepte vacío.
    if (nombreVacio) {
      setError("Tu bot necesita un nombre: es con lo que se presenta al cliente.");
      return;
    }
    setGuardando(true); setError(null);
    try {
      const g = await guardarPerfilBot(sb, tenantId, cambios);
      // Se pinta LO QUE QUEDÓ EN LA BASE, no lo que se envió: si RLS filtró la
      // escritura, el usuario tiene que ver el valor real, no su intención.
      // ⛔ Un `setPerfil({ ...perfil })` aquí es el botón que miente de R-IDE-02.
      // Lo mide V-18.
      setPerfil(g);
      setCambios({});
      setGuardado(true);
    } catch (e) {
      setError(explicarError(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="h-full flex flex-col min-w-0">
      <header className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-borde-suave">
        <h1 className="text-[19px] font-semibold tracking-tight">Ajustes del bot</h1>
        <p className="text-[12.5px] text-tinta-2 mt-0.5">Cómo trata a tus clientes.</p>
      </header>

      <div className="flex-1 overflow-y-auto scroll-fino px-4 sm:px-6 py-4">
        <div className="max-w-[680px] mx-auto w-full space-y-4">
          {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}
          {!puedeEditar && (
            <Aviso tono="alerta">
              Puedes ver la configuración, pero cambiarla es cosa del dueño del negocio.
            </Aviso>
          )}
          {cargando && !perfil && <SkeletonLista filas={3} />}

          {perfil && (<>
            {/* ── Quién es tu bot (R-IDE-01) ───────────────────────────────── */}
            <section className="border border-borde rounded-xl bg-superficie p-4" data-seccion="identidad">
              <h2 className="flex items-center gap-2 text-[14px] font-semibold mb-1">
                <span className="text-tinta-2"><UserRound size={15} /></span>
                Quién es tu bot
              </h2>
              <p className="text-[12.5px] text-tinta-2 leading-snug mb-2">
                Lo que cambies aquí lo usa el bot en su siguiente respuesta, también en WhatsApp.
                Pruébalo en «Probar bot».
              </p>

              {/* ⛔ RECORRIDO, no seis bloques a mano. Ver la nota de la cabecera. */}
              {CAMPOS_DE_IDENTIDAD.map((campo) => {
                const d = DESCRIPTORES[campo];
                if (d.tipo === "interruptor") {
                  return (
                    <Interruptor
                      key={campo} testId={campo}
                      activo={perfil[campo] as boolean}
                      deshabilitado={!puedeEditar}
                      onCambiar={(v) => editar(campo, v as PerfilBot[typeof campo])}
                      etiqueta={d.etiqueta}
                      detalle={d.ayuda}
                    />
                  );
                }
                if (d.tipo === "lista") {
                  return (
                    <Lista
                      key={campo} testId={campo}
                      etiqueta={d.etiqueta} ayuda={d.ayuda} opciones={d.opciones}
                      valor={String(perfil[campo] ?? "")}
                      deshabilitado={!puedeEditar}
                      onCambiar={(v) => editar(campo, v as PerfilBot[typeof campo])}
                    />
                  );
                }
                return (
                  <Campo
                    key={campo} testId={campo}
                    etiqueta={d.etiqueta} ayuda={d.ayuda} maxLength={d.max}
                    requerido={d.requerido} placeholder={d.ejemplo}
                    invalido={campo === "nombre_bot" && nombreVacio}
                    valor={String(perfil[campo] ?? "")}
                    deshabilitado={!puedeEditar}
                    // `firma` y `cargo` son `string | null` en la tabla: vacío se
                    // guarda como `null`, no como `""`, porque el motor distingue
                    // «no hay cargo» de «hay un cargo vacío» (`plantilla_base.ts`).
                    onCambiar={(v) => editar(
                      campo,
                      (d.requerido ? v : (v.trim() === "" ? null : v)) as PerfilBot[typeof campo],
                    )}
                  />
                );
              })}
            </section>

            {/* ── T-ENT-GUION-1 · Presentación y estilo (migr 0088) ──────────────
                ⛔ FUERA de `CAMPOS_DE_IDENTIDAD` a propósito: V-19 exige que todo lo de esa
                lista llegue al system prompt, y estas dos casillas el motor todavía NO las
                lee (se conectan al mudar el guion, tramo 2). Por eso el aviso lo dice. */}
            <section className="border border-borde rounded-xl bg-superficie p-4" data-seccion="presentacion-estilo">
              <h2 className="text-[14px] font-semibold mb-1">Presentación y estilo</h2>
              <p className="text-[12.5px] text-tinta-2 leading-snug mb-2" data-aviso-presentacion>
                Estas dos casillas empiezan a usarse cuando se mude el guion (próximo paso). Hoy el bot
                todavía no las lee.
              </p>
              {([
                ["presentacion", "Presentación del negocio", "Qué es el negocio y qué vende, en dos o tres frases."],
                ["estilo", "Estilo", "Cómo habla: frases cortas, qué palabras usa y cuáles evita."],
              ] as const).map(([campo, etiqueta, ayuda]) => (
                <div key={campo} className="py-2">
                  <label className="block text-[14px] font-medium mb-1">
                    {etiqueta}<span className="text-tinta-2 font-normal"> (opcional)</span>
                  </label>
                  <textarea rows={3} maxLength={2000} data-campo={campo} disabled={!puedeEditar}
                    value={String(perfil[campo] ?? "")}
                    onChange={(e) => editar(campo, (e.target.value.trim() === "" ? null : e.target.value) as PerfilBot[typeof campo])}
                    className="w-full px-3 py-2 rounded-lg bg-elevado text-tinta text-sm border border-borde
                      focus:outline-none focus:ring-2 focus:ring-marca/40 disabled:opacity-50 disabled:cursor-not-allowed" />
                  <p className="text-[12px] text-tinta-2 mt-1">{ayuda}</p>
                </div>
              ))}
            </section>

            {/* ── Mientras el cliente espera ───────────────────────────────── */}
            <section className="border border-borde rounded-xl bg-superficie p-4">
              <h2 className="flex items-center gap-2 text-[14px] font-semibold mb-3">
                <span className="text-tinta-2"><Keyboard size={15} /></span>
                Mientras el cliente espera
              </h2>
              <Interruptor
                activo={perfil.indicador_escritura}
                deshabilitado={!puedeEditar}
                testId="indicador_escritura"
                onCambiar={(v) => editar("indicador_escritura", v)}
                etiqueta="Mostrarle «escribiendo…» al cliente"
                detalle={<>
                  Entre el mensaje del cliente y la respuesta pasan varios segundos —más si mandó
                  una nota de voz—. Con esto encendido ve que lo estás atendiendo, en vez de una
                  pantalla quieta. <strong>Incluye el visto azul</strong>: WhatsApp manda las dos
                  cosas juntas y no se pueden separar.
                </>}
              />
            </section>

            {/* Se dice lo que NO se puede tocar aquí y a quién pedírselo. Callarlo
                dejaría al dueño buscando una pantalla que no existe.
                ⚠️ T-IDE-8: este párrafo YA NO nombra el nombre ni el tono — esos
                dos acaban de subir a la sección de arriba, y dejarlos aquí sería
                mandar a escribir por algo que el dueño ya puede cambiar solo.
                ⚠️ T-CON-9 (1-sep): por lo mismo sale «lo que sabe de tu negocio»
                — eso ya se edita en «Lo que sabe tu bot». Quedan el guion y las
                reglas de precio, que siguen siendo solo-Victorix por RLS. */}
            <p className="text-[12.5px] text-tinta-2 leading-relaxed px-1">
              El guion del bot y las reglas de precio los configura Victorix por ahora. Si quieres
              cambiar alguno, escríbenos. Lo que tu bot sabe del negocio —horario, envíos,
              garantía…— lo cambias tú en «Lo que sabe tu bot».
            </p>

            {puedeEditar && (
              <div className="flex items-center gap-3 pb-2">
                <Boton tono="marca" onClick={guardar} disabled={guardando}>
                  {guardando ? "Guardando…" : "Guardar cambios"}
                </Boton>
                {guardado && (
                  <span className="entra flex items-center gap-1 text-[13px] text-ok">
                    <Check size={15} /> Guardado
                  </span>
                )}
              </div>
            )}
          </>)}
        </div>
      </div>
    </div>
  );
}
